import type { PlayerColor } from '@/shared/board/types'
import {
  HOME_LENGTH,
  LAP_LENGTH,
  QUARTER,
  RING_LENGTH,
  startIndex,
  TOKENS_PER_PLAYER,
} from '@/shared/engine/board'
import type { BonusField } from '@/shared/engine/bonus'
import { type GameState, isShielded, tokensOf } from '@/shared/engine/state'
import { progress } from '@/shared/engine/track'

/**
 * Позиция глазами игрока — вход сети.
 *
 * Доска поворачивается к тому, чьими глазами смотрим: его старт — клетка 0,
 * соперники идут местами 1..3 в порядке хода после него. Тогда одна и та
 * же расстановка за любой цвет выглядит для сети одинаково, и учиться ей
 * вчетверо меньше. Кольцо у всех общее и лежит в одной системе отсчёта:
 * «соперник в трёх клетках позади» — это просто соседние входы, без
 * пересчёта из его координат в мои.
 *
 * Вход разреженный: из восьмисот с лишним чисел ненулевых — несколько
 * десятков (фишки, бонусы, пара счётчиков). Поэтому он отдаётся списком
 * номеров и значений, и сеть считает только их.
 */

const SEATS = 4

/** Клетки кольца, дом, двор «по одной» (1..5 фишек), сдвиг старта, счётчики. */
const SEAT_RING = 0
const SEAT_HOME = SEAT_RING + RING_LENGTH
const SEAT_BASE = SEAT_HOME + HOME_LENGTH
const SEAT_OFFSET = SEAT_BASE + TOKENS_PER_PLAYER
const SEAT_PROGRESS = SEAT_OFFSET + SEATS
const SEAT_PRESENT = SEAT_PROGRESS + 1
const SEAT_SIZE = SEAT_PRESENT + 1

/** Бонусы на кольце — по плоскости на вид. */
const BONUS_KINDS = ['roll', 'shield', 'chest'] as const
const BONUS = SEATS * SEAT_SIZE
/** Фишка под щитом на клетке (чья — видно по плоскостям мест). */
const SHIELD = BONUS + BONUS_KINDS.length * RING_LENGTH
/** Сколько ещё бросков у ходящего в этом ходу: 1 и 2 — по входу. */
const PENDING = SHIELD + RING_LENGTH

export const INPUTS = PENDING + 2

/** Полный путь фишки: двор 0, дом в конце — для счётчика продвижения. */
const FULL = TOKENS_PER_PLAYER * (LAP_LENGTH + HOME_LENGTH + 1)

export type Sparse = { index: number[]; value: number[] }

/**
 * Места за столом глазами игрока: он сам, дальше — по порядку хода.
 * Пустые места (вдвоём, втроём) остаются null.
 */
export function seatsOf(state: GameState, me: PlayerColor): (PlayerColor | null)[] {
  const at = state.players.indexOf(me)
  const seats: (PlayerColor | null)[] = [null, null, null, null]

  state.players.forEach((_, offset) => {
    seats[offset] = state.players[(at + offset) % state.players.length]
  })

  return seats
}

/**
 * @param pending броски, которые у ходящего ещё впереди в этом ходу (углы,
 *   бонусы): ещё один бросок — это лишний темп, и сеть должна его видеть.
 */
export function encode(
  state: GameState,
  field: BonusField | null,
  me: PlayerColor,
  pending: number,
): Sparse {
  const index: number[] = []
  const value: number[] = []
  const put = (at: number, v: number) => {
    index.push(at)
    value.push(v)
  }
  const origin = startIndex(me)
  const frame = (cell: number) => (cell - origin + RING_LENGTH) % RING_LENGTH

  seatsOf(state, me).forEach((color, seat) => {
    if (color === null) return

    const at = seat * SEAT_SIZE
    let inBase = 0
    let walked = 0

    for (const [token, position] of tokensOf(state, color).entries()) {
      if (position.location === 'base') {
        inBase++
        continue
      }

      if (position.location === 'home') {
        put(at + SEAT_HOME + position.index, 1)
        walked += LAP_LENGTH + position.index + 2
        continue
      }

      put(at + SEAT_RING + frame(position.index), 1)
      walked += progress(color, position.index) + 1

      if (isShielded(state, { color, index: token })) put(SHIELD + frame(position.index), 1)
    }

    // Двор — «по одной»: вход k горит, если во дворе не меньше k фишек.
    // Так «одна во дворе» и «пять во дворе» различаются не масштабом
    // одного числа, а набором входов, и сети проще.
    for (let k = 0; k < inBase; k++) put(at + SEAT_BASE + k, 1)

    put(at + SEAT_OFFSET + ((startIndex(color) - origin + RING_LENGTH) % RING_LENGTH) / QUARTER, 1)
    put(at + SEAT_PROGRESS, walked / FULL)
    put(at + SEAT_PRESENT, 1)
  })

  for (const item of field?.items ?? []) {
    const kind = BONUS_KINDS.indexOf(item.kind as (typeof BONUS_KINDS)[number])

    if (kind !== -1) put(BONUS + kind * RING_LENGTH + frame(item.index), 1)
  }

  if (pending >= 1) put(PENDING, 1)
  if (pending >= 2) put(PENDING + 1, 1)

  return { index, value }
}
